// SBG Co. theme script, one file for every page: the bag count, quick add, the phone menu and search,
// the email form, and the product page (gallery, buy box, buy bar, size guide). Talks to Shopify's
// own endpoints: /products/<handle>.js, /cart/add.js, /cart.js. State is replaced, never edited.
(function () {
  'use strict';

  const SHOP = window.SBG_SHOP || {};
  const ROOT = SHOP.productJson || '/';
  const CART_ADD = `${SHOP.cartAdd || '/cart/add'}.js`;
  const CART_JSON = SHOP.cartJson || '/cart.js';
  const TOAST_MS = 3200;
  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const SHORT_SIZE = { small: 'S', medium: 'M', large: 'L', 'extra large': 'XL' };
  const HTML_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  const SORRY = 'That one is not available right now. Try again in a moment.';
  // Garment colours by name: the client's own swatches first, then approximations. Keep in step with
  // snippets/sbg-color.liquid, which draws the same dots on the server.
  const COLOR_HEX = {
    ash: '#ebeaef', 'ash grey': '#ebeaef', 'ash gray': '#ebeaef', brown: '#7e6455', 'brown tee': '#7e6455',
    'brown sweatshirt': '#443c36', 'dark brown': '#443c36', cream: '#f0e8dc', natural: '#f0e8dc', 'cream or natural': '#f0e8dc', maroon: '#69303f',
    mauve: '#ad6867', 'military green': '#716d51', navy: '#222c44', 'neon pink': '#fc80a4', pepper: '#525254',
    spruce: '#5e746d', 'blue spruce': '#5e746d', 'washed denim': '#889db8', denim: '#889db8', black: '#1f1c1c',
    white: '#ffffff', beige: '#e3d3b8', 'peppered black': '#2b2927', ivory: '#f4ecd8', grey: '#a9a7a3', gray: '#a9a7a3',
    'heather grey': '#b9b7b4', 'heather gray': '#b9b7b4', 'sport grey': '#b3b1ae', 'sport gray': '#b3b1ae',
    'light grey': '#d5d3cf', 'light gray': '#d5d3cf', charcoal: '#47443f', graphite: '#4b4a4a', granite: '#8b8a88',
    pink: '#f2a7b8', 'light pink': '#f6c9d2', blush: '#f1c6bd', blossom: '#f4c3cb', red: '#b3262b', 'dark red': '#7d1f25',
    burgundy: '#6e2233', crimson: '#9b2335', berry: '#7f3657', watermelon: '#e2707a', blue: '#3c5a8c',
    'light blue': '#a9c6e2', 'blue jean': '#64809c', chambray: '#a9c0dc', 'ice blue': '#cfe0e6', 'flo blue': '#4f6fb6',
    turquoise: '#3fb5b8', seafoam: '#9fd4c4', 'chalky mint': '#a8ddd0', bay: '#9db3a3', sage: '#a7b39a',
    'forest green': '#2f4a3a', green: '#4d7a55', moss: '#6b6f45', olive: '#6a6a3c', espresso: '#4a362c', tan: '#c8ab84',
    sand: '#d9c7a8', khaki: '#c2b28b', mustard: '#d2a23a', yellow: '#f0d264', butter: '#f6e6a6', banana: '#f7e7a0',
    orange: '#e0853c', rust: '#a9552f', terracotta: '#c0714f', yam: '#c9683a', peachy: '#f6bfa0', peach: '#f6bfa0',
    purple: '#6d4f8c', lavender: '#c6b6de', orchid: '#d7b8d6', camo: '#6b6a4c', unfinished: '#d8c3a0',
  };
  const FALLBACK_HEX = '#cbb9a0';

  const esc = (value) => String(value).replace(/[&<>"']/g, (c) => HTML_ESCAPES[c]);
  const shortSize = (s) => String(s)
    .replace(/\b(extra large|small|medium|large)\b/gi, (m) => SHORT_SIZE[m.toLowerCase()])
    .replace(/^youth/i, 'Youth');
  // Youth sizes are written the way the trade writes them (YS, YM), so four chips fit a row on a phone.
  const chipText = (label) => shortSize(label).replace(/^Youth /, 'Y');
  const colorHex = (name) => COLOR_HEX[String(name).toLowerCase().trim()] || FALLBACK_HEX;
  const isColor = (name) => /colou?r/i.test(name);
  const isSize = (name) => /size/i.test(name);
  // Prices come in cents. Whole dollars show without cents, the way the shop writes them.
  const SYMBOL = (String(SHOP.moneyFormat || '${{amount}}').replace(/<[^>]+>/g, '').split('{{')[0] || '$').trim();
  const money = (cents) => {
    const n = Math.round(Number(cents) || 0) / 100;
    return `${SYMBOL}${Number.isInteger(n) ? n : n.toFixed(2)}`;
  };
  const sized = (url, width) => (url ? `${String(url).replace(/^\/\//, 'https://')}${url.includes('?') ? '&' : '?'}width=${width}` : '');
  // Size charts are kept in inches; eighths print as fractions, the way a tape measure reads.
  const EIGHTHS = ['', '⅛', '¼', '⅜', '½', '⅝', '¾', '⅞'];
  const inches = (n) => {
    const whole = Math.floor(n);
    const eighths = Math.round((n - whole) * 8);
    return eighths === 8 ? String(whole + 1) : `${whole || ''}${EIGHTHS[eighths]}` || '0';
  };
  const measure = (n, unit) => (typeof n !== 'number' ? esc(n) : unit === 'cm' ? (n * 2.54).toFixed(1) : inches(n));

  // ----- toast -----
  const toast = document.getElementById('toast');
  let toastTimer = 0;
  function say(message) {
    if (!toast) return;
    toast.textContent = message;
    toast.setAttribute('data-show', '');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast.removeAttribute('data-show'), TOAST_MS);
  }

  // ----- bag -----
  function writeBag(count) {
    const badge = document.getElementById('bag-count');
    const link = document.getElementById('bag-link');
    if (badge) { badge.textContent = String(count); badge.hidden = count === 0; }
    if (link) link.setAttribute('aria-label', count === 0 ? 'Bag, empty' : `Bag, ${count} ${count === 1 ? 'item' : 'items'}`);
  }
  async function refreshBag() {
    try {
      const res = await fetch(CART_JSON, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      writeBag(Number(cart.item_count) || 0);
    } catch (err) {
      // Offline or blocked: the count printed with the page stands.
    }
  }
  async function addToCart(variantId) {
    const res = await fetch(CART_ADD, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.description || body.message || 'Could not add that to your bag.');
    await refreshBag();
    document.dispatchEvent(new CustomEvent('sbg:cart:added', { detail: body }));
    return body;
  }

  // ----- the cart drawer app (Zipify) and personalised pieces -----
  // Zipify adds an item itself when an add button is pressed. On a personalised piece the personalizer
  // (Zepto) adds it too, with its preview, so one press put the piece in the bag twice (the old theme
  // does the same). Zipify's own switch is a selector of buttons it leaves alone, read at each press;
  // it is set here before Zipify's document listeners hear the press. Its script replaces its settings
  // object when it loads, so the selector is set again on every press rather than once. The quick add
  // sheet's button is skipped too: its form holds the picks, not a variant id, so Zipify's own add
  // failed and ours never ran (on the live theme, 2026-09-28).
  const SKIP_DRAWER = '[data-drawer-skip]';
  const withSkip = (base) => (base && String(base).includes(SKIP_DRAWER) ? String(base) : [base, SKIP_DRAWER].filter(Boolean).join(', '));
  function skipDrawer() {
    // Zipify's objects are a third party's: if one ever refuses the write, the page's own script carries on.
    try {
      const zipify = window.Zipify;
      if (!zipify) return;
      if (zipify.Cart && zipify.Cart.api) zipify.Cart.api.excludeActionButton = withSkip(zipify.Cart.api.excludeActionButton);
      const upsell = zipify.OCU && zipify.OCU.api;
      if (upsell) {
        const saved = upsell.store && typeof upsell.store.get === 'function' ? upsell.store.get('excludeAddToCartButton') : '';
        upsell.excludeActionButton = withSkip(upsell.excludeActionButton || saved);
      }
    } catch (err) {
      // Left as Zipify set it.
    }
  }
  if (document.querySelector(SKIP_DRAWER) || document.getElementById('quick-add')) {
    ['pointerdown', 'touchstart', 'mousedown', 'keydown', 'click'].forEach((type) => window.addEventListener(type, skipDrawer, { capture: true, passive: true }));
    skipDrawer();
  }

  // ----- dialogs -----
  function wireDialog(dialog) {
    if (!dialog) return;
    dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });
    dialog.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', () => dialog.close()));
  }
  const menu = document.getElementById('menu');
  const quick = document.getElementById('quick-add');
  const guide = document.getElementById('size-guide');
  [menu, quick, guide].forEach(wireDialog);
  document.querySelectorAll('[data-open-menu]').forEach((b) => b.addEventListener('click', () => menu && menu.showModal()));
  document.querySelectorAll('[data-open-guide]').forEach((b) => b.addEventListener('click', () => guide && guide.showModal()));

  // ----- the shop menu's panels: the chevron opens one (keyboard, touch); a click elsewhere or Escape closes it -----
  const drops = [...document.querySelectorAll('[data-drop]')];
  const closeDrop = (item) => {
    item.removeAttribute('data-open');
    const button = item.querySelector('.nav__more');
    if (button) button.setAttribute('aria-expanded', 'false');
  };
  drops.forEach((item) => {
    const button = item.querySelector('.nav__more');
    if (!button) return;
    button.addEventListener('click', () => {
      const opening = !item.hasAttribute('data-open');
      drops.forEach(closeDrop);
      item.toggleAttribute('data-open', opening);
      button.setAttribute('aria-expanded', String(opening));
    });
  });
  document.addEventListener('click', (e) => { drops.filter((item) => !item.contains(e.target)).forEach(closeDrop); });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const open = drops.find((item) => item.hasAttribute('data-open'));
    if (!open) return;
    closeDrop(open);
    const button = open.querySelector('.nav__more');
    if (button) button.focus();
  });

  // ----- phone search: the bar shows its search icon once the search field under it is out of sight -----
  // The bar sticks to the top, so the field is hidden the moment it slides under the bar: the watch
  // starts at the bar's lower edge, not at the top of the screen.
  const searchRow = document.querySelector('.search-row');
  if (searchRow && 'IntersectionObserver' in window) {
    const bar = document.querySelector('.head');
    const under = bar ? Math.round(bar.getBoundingClientRect().height) : 0;
    const watchSearch = new IntersectionObserver(([entry]) => {
      document.body.toggleAttribute('data-search-row', entry.isIntersecting);
    }, { rootMargin: `-${under}px 0px 0px 0px` });
    watchSearch.observe(searchRow);
  }

  // ----- products from Shopify's JSON endpoint, once each -----
  const productCache = new Map();
  async function fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    const res = await fetch(`${ROOT}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`${res.status} for ${handle}`);
    const product = await res.json();
    productCache.set(handle, product);
    return product;
  }
  // The variant whose options are exactly the picks, in the product's option order.
  function variantFor(product, picks) {
    const names = product.options.map((o) => (typeof o === 'string' ? o : o.name));
    if (!names.every((name) => picks[name])) return null;
    return product.variants.find((v) => names.every((name, i) => v.options[i] === picks[name])) || null;
  }
  // Whether some variant with these picks (the rest open) can be bought.
  function anyAvailable(product, picks) {
    const names = product.options.map((o) => (typeof o === 'string' ? o : o.name));
    return product.variants.some((v) => v.available && names.every((name, i) => !picks[name] || v.options[i] === picks[name]));
  }
  // An option with one value across every variant ("Title: Default Title" on a product with no
  // choices, a sign that comes in one finish) is not a choice: it is picked from the start.
  function fixedPicks(product) {
    const names = product.options.map((o) => (typeof o === 'string' ? o : o.name));
    return names.reduce((picks, name, i) => {
      const values = [...new Set(product.variants.map((v) => v.options[i]))];
      return values.length === 1 ? { ...picks, [name]: values[0] } : picks;
    }, {});
  }
  // Products whose page holds something quick add cannot: a bundle builder, or a name to engrave.
  function needsItsPage(product) {
    const tags = (product.tags || []).map((t) => String(t).toLowerCase());
    const names = product.options.map((o) => String(typeof o === 'string' ? o : o.name).toLowerCase());
    // "Custom made" means made to order, not a name to engrave (as in snippets/sbg-kind.liquid).
    const words = `${product.handle} ${product.title}`.replace(/custom[ -]made/gi, '');
    return tags.some((t) => t.includes('bundle')) || /custom|personali[sz]/i.test(words) || names.some((n) => n.includes('personali'));
  }

  // ----- quick add -----
  // The colour that opens is the first one some size is in stock in, so the sheet never opens on a sold-out colour.
  function firstInStock(option, product) {
    const i = option.position - 1;
    return option.values.find((value) => product.variants.some((v) => v.available && v.options[i] === value)) || option.values[0];
  }
  function optionsHtml(option, product) {
    const kind = isColor(option.name) ? 'color' : isSize(option.name) ? 'size' : 'text';
    const field = `option-${option.position}`;
    const opening = kind === 'color' ? firstInStock(option, product) : '';
    const chips = option.values.map((value) => {
      const label = kind === 'color'
        ? `<span style="--c:${colorHex(value)}" title="${esc(value)}"></span>`
        : `<span>${esc(kind === 'size' ? chipText(value) : value)}</span>`;
      const checked = kind === 'color' && value === opening ? ' checked' : '';
      return `<label class="opt${kind === 'color' ? ' opt--color' : ''}"><input type="radio" name="${field}" value="${esc(value)}" aria-label="${esc(value)}"${checked}>${label}</label>`;
    }).join('');
    const legend = kind === 'color' ? `Color <span data-qa-color>${esc(opening)}</span>` : esc(option.name);
    return `<fieldset data-option="${esc(option.name)}"${kind === 'color' ? '' : ' data-required'}><legend>${legend}</legend><div class="opts">${chips}</div></fieldset>`;
  }
  // What the button says while a required choice is still open: "Pick a size", "Pick a size and your vibe".
  // Only real choices count: an option with one value is already picked.
  function needsLabel(product) {
    const open = product.options.filter((o) => o.values.length > 1 && !isColor(o.name)).map((o) => (isSize(o.name) ? 'a size' : o.name.toLowerCase()));
    return open.length ? `Pick ${open.join(' and ')}` : '';
  }
  function quickAddHtml(p) {
    const needs = needsLabel(p);
    const compare = p.compare_at_price_max > p.price_min ? `<s>${money(p.compare_at_price_max)}</s>` : '';
    const options = p.options.filter((o) => o.values.length > 1).map((o) => optionsHtml(o, p)).join('');
    return `<div class="qa__product">
  <img src="${esc(sized(p.featured_image, 240))}" alt="" width="240" height="300">
  <div><b>${esc(p.title)}</b><span class="price">${p.price_varies ? 'From ' : ''}${money(p.price_min)}${compare}</span></div>
</div>
${options}
<div class="qa__foot">
  <button class="btn btn--wide" type="submit" data-drawer-skip data-needs="${esc(needs)}"${needs ? ' disabled' : ''}>${esc(needs || 'Add to bag')}</button>
  <p class="qa__note"><a class="link" href="${ROOT}products/${esc(p.handle)}">See full details</a></p>
</div>`;
  }
  function quickPicks(form, product) {
    return [...form.querySelectorAll('fieldset[data-option]')].reduce((picks, f) => {
      const chosen = f.querySelector('input:checked');
      return chosen ? { ...picks, [f.dataset.option]: chosen.value } : picks;
    }, fixedPicks(product));
  }
  async function openQuickAdd(handle) {
    if (!quick) return;
    let product;
    try { product = await fetchProduct(handle); } catch (err) { say(SORRY); return; }
    if (needsItsPage(product)) { window.location.href = `${ROOT}products/${encodeURIComponent(handle)}`; return; }
    const form = quick.querySelector('form');
    form.innerHTML = quickAddHtml(product);
    form.dataset.handle = handle;
    quick.showModal();
  }
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-quick-add]');
    if (trigger) openQuickAdd(trigger.getAttribute('data-quick-add'));
  });
  if (quick) {
    const form = quick.querySelector('form');
    form.addEventListener('change', (e) => {
      const fieldset = e.target.closest('fieldset[data-option]');
      if (fieldset && isColor(fieldset.dataset.option)) {
        const name = form.querySelector('[data-qa-color]');
        if (name) name.textContent = e.target.value;
      }
      const submit = form.querySelector('[type="submit"]');
      const open = [...form.querySelectorAll('fieldset[data-required]')].filter((f) => !f.querySelector('input:checked'));
      const product = productCache.get(form.dataset.handle);
      const variant = product && open.length === 0 ? variantFor(product, quickPicks(form, product)) : null;
      const soldOut = open.length === 0 && (!variant || !variant.available);
      submit.disabled = open.length > 0 || soldOut;
      submit.textContent = open.length ? submit.dataset.needs : soldOut ? 'Sold out' : 'Add to bag';
    });
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const product = productCache.get(form.dataset.handle);
      const picks = product ? quickPicks(form, product) : {};
      const variant = product ? variantFor(product, picks) : null;
      if (!variant) { say(SORRY); return; }
      const submit = form.querySelector('[type="submit"]');
      submit.disabled = true;
      try {
        await addToCart(variant.id);
        quick.close();
        const parts = [product.title, ...product.options.filter((o) => o.values.length > 1).map((o) => (isSize(o.name) ? shortSize(picks[o.name]) : picks[o.name]))].filter(Boolean);
        say(`Added to your bag: ${parts.join(', ')}`);
      } catch (err) {
        say(err.message || SORRY);
      } finally {
        submit.disabled = false;
      }
    });
  }

  // ----- the email list: Shopify's own form, checked before it goes -----
  const list = document.getElementById('list-form');
  if (list) {
    const input = list.querySelector('input[type="email"]');
    const note = document.getElementById('list-note');
    const tell = (tone, message) => { if (note) { note.dataset.tone = tone; note.textContent = message; } };
    list.addEventListener('submit', (e) => {
      if (!input || EMAIL.test(input.value.trim())) return;
      e.preventDefault();
      input.setAttribute('aria-invalid', 'true');
      tell('error', 'That does not look like an email address. Try one like name@example.com.');
      input.focus();
    });
    if (input) {
      input.addEventListener('input', () => {
        if (input.getAttribute('aria-invalid')) { input.removeAttribute('aria-invalid'); tell('', 'Unsubscribe any time.'); }
      });
    }
  }

  // ----- photos of a garment on white, placed so the whole garment shows -----
  // A photo whose four corners are near white is a packshot. It is placed in its 4:5 frame as large
  // as the whole garment allows, on a mat in the photo's own edge colours. Ported from
  // tools/pull-product.mjs, which did the same for the mockups.
  const NEAR_WHITE = 225;
  const FRAME = 4 / 5;
  const BREATHING = 0.06;
  const LIGHT_GARMENT = 200;
  const MAX_LIFT = 1.06;
  const SAMPLE_W = 160;
  function readPhoto(img) {
    const W = SAMPLE_W;
    const H = Math.max(1, Math.round((W * img.naturalHeight) / img.naturalWidth));
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, W, H);
    const data = ctx.getImageData(0, 0, W, H).data; // throws when the image is not shared with CORS
    const at = (x, y) => { const i = (y * W + x) * 4; return [data[i], data[i + 1], data[i + 2]]; };
    const corners = [at(2, 2), at(W - 3, 2), at(2, H - 3), at(W - 3, H - 3)];
    if (!corners.every((c) => Math.min(...c) >= NEAR_WHITE)) return { packshot: false };
    const ground = Math.min(...corners.map((c) => Math.min(...c)));
    const box = { x0: W, x1: -1, y0: H, y1: -1 };
    let lumaSum = 0;
    let garmentPixels = 0;
    for (let y = 0; y < H; y += 1) {
      for (let x = 0; x < W; x += 1) {
        const [r, g, b] = at(x, y);
        const garment = Math.max(r, g, b) - Math.min(r, g, b) > 28 || Math.min(r, g, b) < ground - 12;
        if (!garment) continue;
        box.x0 = Math.min(box.x0, x); box.x1 = Math.max(box.x1, x);
        box.y0 = Math.min(box.y0, y); box.y1 = Math.max(box.y1, y);
        lumaSum += 0.2126 * r + 0.7152 * g + 0.0722 * b;
        garmentPixels += 1;
      }
    }
    const rowColor = (y) => {
      const sum = [0, 0, 0];
      for (let x = 0; x < W; x += 1) at(x, y).forEach((v, k) => { sum[k] += v; });
      return sum.map((v) => Math.round(v / W));
    };
    return {
      packshot: true,
      aspect: img.naturalWidth / img.naturalHeight,
      box: box.x1 < 0 ? null : { x0: box.x0 / W, x1: (box.x1 + 1) / W, y0: box.y0 / H, y1: (box.y1 + 1) / H },
      top: rowColor(1),
      bottom: rowColor(H - 2),
      garmentLuma: garmentPixels ? lumaSum / garmentPixels : 255,
    };
  }
  function placement(photo) {
    if (!photo.packshot || !photo.box) return null;
    const frameH = 1 / FRAME;
    const { x0, x1, y0, y1 } = photo.box;
    const between = (n, low, high) => Math.max(Math.min(low, high), Math.min(Math.max(low, high), n));
    const width = Math.min((1 - 2 * BREATHING) / (x1 - x0), ((frameH - 2 * BREATHING * frameH) * photo.aspect) / (y1 - y0));
    const height = width / photo.aspect;
    const x = between(0.5 - ((x0 + x1) / 2) * width, 1 - width, 0);
    const y = between(frameH / 2 - ((y0 + y1) / 2) * height, frameH - height, 0);
    const pct = (n) => `${(Math.max(0, Math.min(1, n)) * 100).toFixed(2)}%`;
    const groundLevel = Math.min(...photo.top, ...photo.bottom);
    const lift = photo.garmentLuma < LIGHT_GARMENT ? Math.min(MAX_LIFT, 255 / groundLevel) : 1;
    const lifted = (rgb) => `rgb(${rgb.map((v) => Math.min(255, Math.round(v * lift))).join(', ')})`;
    const mat = `linear-gradient(${lifted(photo.top)} ${pct(y / frameH)}, ${lifted(photo.bottom)} ${pct((y + height) / frameH)})`;
    return { w: `${(width * 100).toFixed(2)}%`, x: `${(x * 100).toFixed(2)}%`, y: `${((y / frameH) * 100).toFixed(2)}%`, mat, lift: lift > 1.004 ? lift.toFixed(3) : '' };
  }
  function applyPlacement(frame, place) {
    if (!frame || !place) return;
    frame.style.setProperty('--w', place.w);
    frame.style.setProperty('--x', place.x);
    frame.style.setProperty('--y', place.y);
    frame.style.setProperty('--mat', place.mat);
    if (place.lift) frame.style.setProperty('--lift', place.lift);
    frame.setAttribute('data-placed', '');
  }
  function placePhotos(photos, thumbs) {
    if (!photos) return;
    [...photos.children].forEach((frame, i) => {
      const img = frame.querySelector('img[data-sample]');
      if (!img) return;
      const sample = new Image();
      sample.crossOrigin = 'anonymous';
      sample.src = img.getAttribute('data-sample');
      sample.decode().then(() => {
        const place = placement(readPhoto(sample));
        applyPlacement(frame, place);
        applyPlacement(thumbs && thumbs.children[i] ? thumbs.children[i].querySelector('[data-frame]') : null, place);
      }).catch(() => { /* the photo could not be read: it fills its frame instead */ });
    });
  }

  // ----- reviews: the store's Judge.me reviews, drawn in the page's own style -----
  // Judge.me's widget script does not draw into this theme, so the reviews are read from the same
  // public feed its widget uses and laid out like the mockup: the spread of scores beside the quotes.
  const REVIEWS_SHOWN = 6;
  const STAR = '<svg aria-hidden="true"><use href="#i-star"/></svg>';
  function starsHtml(value, small) {
    const fill = Math.max(0, Math.min(100, (value / 5) * 100)).toFixed(1);
    return `<span class="stars${small ? ' stars--sm' : ''}" style="--fill:${fill}%" aria-hidden="true">${STAR.repeat(5)}<span class="stars__on">${STAR.repeat(5)}</span></span>`;
  }
  const plainText = (html) => new DOMParser().parseFromString(String(html || ''), 'text/html').body.textContent.replace(/\s+/g, ' ').trim();
  async function renderReviews(box) {
    const shop = box.getAttribute('data-shop');
    const productId = box.getAttribute('data-product-id');
    const spread = document.querySelector('[data-spread]');
    if (!shop || !productId) return;
    try {
      const url = `https://judge.me/reviews/reviews_for_widget?url=${encodeURIComponent(shop)}&shop_domain=${encodeURIComponent(shop)}&platform=shopify&page=1&per_page=${REVIEWS_SHOWN}&product_id=${encodeURIComponent(productId)}`;
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const feed = await res.json();
      const reviews = (feed.reviews || []).filter((r) => !r.is_anonymous_reviewer && plainText(r.body_html)).slice(0, REVIEWS_SHOWN);
      box.innerHTML = reviews.map((r) => `<figure class="review review--plain">
  <blockquote>${esc(plainText(r.body_html))}</blockquote>
  <figcaption class="review__who">${starsHtml(Number(r.rating) || 0, true)}<span><b>${esc(r.reviewer_name || 'A buyer')}</b>${r.verified_buyer ? 'Verified buyer' : 'Reviewer'}</span></figcaption>
</figure>`).join('');
      if (spread && Array.isArray(feed.histogram)) {
        spread.innerHTML = feed.histogram.map((h) => `<li><span>${esc(h.rating)}</span><i><b style="width:${Math.max(0, Math.min(100, Number(h.percentage) || 0))}%"></b></i><span>${esc(h.frequency)}</span></li>`).join('');
      }
    } catch (err) {
      // The feed is a third party's; without it the score above still stands.
    }
  }
  const reviewsBox = document.querySelector('[data-reviews]');
  if (reviewsBox) renderReviews(reviewsBox);

  // ----- the product page -----
  const dataEl = document.querySelector('[data-product-json]');
  const buyForm = document.querySelector('[data-buy]');
  if (dataEl && buyForm) initProduct(JSON.parse(dataEl.textContent), buyForm);

  function initProduct(product, form) {
    const el = {
      photos: document.querySelector('[data-photos]'),
      thumbs: document.querySelector('[data-thumbs]'),
      count: document.querySelector('[data-photo-count]'),
      price: document.querySelector('[data-price]'),
      sezzle: document.querySelector('[data-sezzle]'),
      colorName: document.querySelector('[data-color-name]'),
      variantId: form.querySelector('[data-variant-id]'),
      submit: form.querySelector('[data-add]'),
      express: form.querySelector('[data-express]'),
      bar: document.querySelector('[data-buybar]'),
      barPrice: document.querySelector('[data-bar-price]'),
      barBtn: document.querySelector('[data-bar-btn]'),
    };
    // Each option is a group: radios (colour dots) or one select (the size drop-down).
    const fieldsets = [...form.querySelectorAll('fieldset[data-option]')];
    const names = product.options;
    const fixed = fixedPicks(product);
    const choiceNames = names.filter((name) => !(name in fixed));
    const colorName = names.find(isColor) || '';
    const pickOf = (f) => { const select = f.querySelector('select'); if (select) return select.value; const c = f.querySelector('input:checked'); return c ? c.value : ''; };
    const first = (name) => { const f = fieldsets.find((x) => x.dataset.option === name); return f ? pickOf(f) : ''; };
    const photoIndexFor = (mediaId) => (el.photos ? [...el.photos.children].findIndex((li) => li.getAttribute('data-media-id') === String(mediaId)) : -1);
    // A choice no variant can supply beside the other picks is greyed, not hidden: she can see the range.
    function markAvailable(f, picks) {
      const name = f.dataset.option;
      const others = Object.fromEntries(Object.entries(picks).filter(([k]) => k !== name));
      f.querySelectorAll('input, option[value]:not([value=""])').forEach((choice) => {
        const open = anyAvailable(product, { ...others, [name]: choice.value });
        choice.disabled = !open;
        if (choice.tagName === 'OPTION') {
          const base = choice.dataset.label || choice.textContent.trim();
          choice.dataset.label = base;
          choice.textContent = open ? base : `${base}, sold out`;
        }
      });
      const select = f.querySelector('select');
      if (select) select.toggleAttribute('data-unset', !select.value);
    }

    const initialPicks = fieldsets.reduce((picks, f) => (pickOf(f) ? { ...picks, [f.dataset.option]: pickOf(f) } : picks), fixed);
    let state = Object.freeze({ picks: Object.freeze(initialPicks), photo: 0 });

    // A gift card can go straight to someone: Shopify's own recipient fields, sent only when ticked.
    const recipient = form.querySelector('[data-recipient]');
    if (recipient) {
      const toggle = recipient.querySelector('[data-recipient-toggle]');
      const fields = recipient.querySelector('[data-recipient-fields]');
      const offset = recipient.querySelector('[data-recipient-offset]');
      const sync = () => {
        const on = Boolean(toggle && toggle.checked);
        if (fields) {
          fields.hidden = !on;
          fields.querySelectorAll('input, textarea').forEach((field) => { field.disabled = !on; });
        }
        if (offset) { offset.value = String(new Date().getTimezoneOffset()); offset.disabled = !on; }
      };
      if (toggle) toggle.addEventListener('change', sync);
      sync();
    }

    placePhotos(el.photos, el.thumbs);

    function render(s) {
      const variant = variantFor(product, s.picks);
      const partial = product.variants.filter((v) => names.every((name, i) => !s.picks[name] || v.options[i] === s.picks[name]));
      const prices = (partial.length ? partial : product.variants).map((v) => v.price);
      const price = variant ? variant.price : Math.min(...prices);
      const exact = Boolean(variant) || Math.min(...prices) === Math.max(...prices);
      const label = `${exact ? '' : 'From '}${money(price)}`;
      const missing = names.filter((name) => !s.picks[name]).map((name) => (isSize(name) ? 'a size' : name.toLowerCase()));
      const soldOut = missing.length === 0 && (!variant || !variant.available);
      const button = missing.length ? `Pick ${missing.join(' and ')}` : soldOut ? 'Sold out' : 'Add to bag';

      if (el.price) el.price.textContent = label;
      if (el.sezzle) el.sezzle.textContent = `or 4 payments of ${money(price / 4)} with Sezzle`;
      if (el.colorName && colorName) el.colorName.textContent = s.picks[colorName] || '';
      // The form's variant id: the match, or the first available variant while a choice is still open
      // (app scripts read it for a price; it is disabled then, so a plain submit cannot add the wrong size).
      const fallback = product.variants.find((v) => v.available) || product.variants[0];
      if (el.variantId) {
        const id = String(variant ? variant.id : fallback ? fallback.id : '');
        const moved = el.variantId.value !== id;
        el.variantId.value = id;
        el.variantId.disabled = !variant;
        // Shopify's installments line reads the variant again when this field announces a change.
        if (moved) el.variantId.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (el.submit) {
        el.submit.disabled = missing.length > 0 || soldOut;
        el.submit.textContent = button;
      }
      // Buy with Shop Pay reads the form's variant, so it only appears once every choice is made.
      if (el.express) el.express.hidden = missing.length > 0 || soldOut;
      fieldsets.forEach((f) => markAvailable(f, s.picks));
      if (el.photos) [...el.photos.children].forEach((li, i) => li.toggleAttribute('data-on', i === s.photo));
      if (el.thumbs) el.thumbs.querySelectorAll('button').forEach((b, i) => b.setAttribute('aria-pressed', String(i === s.photo)));
      const chosen = choiceNames.map((name) => (isSize(name) ? shortSize(s.picks[name] || '') : s.picks[name])).filter(Boolean);
      if (el.barPrice) el.barPrice.textContent = missing.length || !chosen.length ? label : `${label}, ${chosen.join(', ')}`;
      if (el.barBtn) el.barBtn.textContent = button;
    }
    function setState(patch) {
      state = Object.freeze({ ...state, ...patch });
      render(state);
    }
    // The address names the chosen variant, as Shopify themes do: the back-in-stock app reads it to offer
    // "Notify me" on a sold-out size, and a shared link opens on the same piece. Only her own picks move it,
    // so a link that arrives with a variant keeps it until she chooses.
    function syncAddress(variant) {
      try {
        const url = new URL(window.location.href);
        const next = variant ? String(variant.id) : null;
        if (url.searchParams.get('variant') === next) return;
        if (next) url.searchParams.set('variant', next);
        else url.searchParams.delete('variant');
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (err) {
        // A browser that refuses the rewrite only costs the apps their cue; the page itself is unaffected.
      }
    }

    // On a phone the photos are a strip she swipes: bring the chosen one into view.
    function showOnStrip(index) {
      const item = el.photos && el.photos.children[index];
      if (item && el.photos.scrollWidth > el.photos.clientWidth) el.photos.scrollTo({ left: item.offsetLeft - el.photos.offsetLeft, behavior: 'smooth' });
    }
    form.addEventListener('change', (e) => {
      const fieldset = e.target.closest('fieldset[data-option]');
      if (!fieldset) return;
      const name = fieldset.dataset.option;
      const picks = Object.freeze(e.target.value ? { ...state.picks, [name]: e.target.value } : Object.fromEntries(Object.entries(state.picks).filter(([k]) => k !== name)));
      // A colour with a photo of its own brings that photo forward.
      const shown = isColor(name) ? product.variants.find((v) => v.options[names.indexOf(name)] === e.target.value && v.media) : null;
      const index = shown ? photoIndexFor(shown.media) : -1;
      setState(index >= 0 ? { picks, photo: index } : { picks });
      syncAddress(variantFor(product, picks));
      if (index >= 0) showOnStrip(index);
    });
    if (el.thumbs) {
      el.thumbs.addEventListener('click', (e) => {
        const button = e.target.closest('[data-photo]');
        if (button) setState({ photo: Number(button.getAttribute('data-photo')) });
      });
    }

    // Anything an app has asked for (a name to engrave, a recipient's email) must be filled in first.
    const labelOf = (field) => {
      const label = field.labels && field.labels[0] ? field.labels[0].innerText.replace(/\s*\*\s*$/, '').trim() : '';
      const named = (String(field.name || '').match(/^properties\[(.+)\]$/) || [])[1];
      return label || field.getAttribute('aria-label') || named || 'the required field';
    };
    const missingRequired = () => [...form.querySelectorAll('[required]')]
      .find((field) => !field.disabled && field.type !== 'hidden' && (field.type === 'checkbox' ? !field.checked : !String(field.value || '').trim()));
    // The form goes to the cart as it stands, so every line property an app has added travels with it.
    async function addFromForm() {
      const body = new FormData();
      for (const [key, value] of new FormData(form).entries()) {
        if (key === 'id' || key === 'quantity' || key === 'selling_plan' || key.startsWith('properties[')) body.append(key, value);
      }
      if (!body.has('quantity')) body.append('quantity', '1');
      const res = await fetch(CART_ADD, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.description || json.message || 'Could not add that to your bag.');
      await refreshBag();
      document.dispatchEvent(new CustomEvent('sbg:cart:added', { detail: json }));
    }

    async function addToBag() {
      const variant = variantFor(product, state.picks);
      if (!variant) {
        const open = fieldsets.find((f) => !pickOf(f));
        if (open) {
          open.scrollIntoView({ block: 'center', behavior: 'smooth' });
          const control = open.querySelector('select, input:not(:disabled)');
          if (control) control.focus({ preventScroll: true });
        }
        return;
      }
      if (!variant.available) { say('That one is sold out right now.'); return; }
      const empty = missingRequired();
      if (empty) {
        empty.scrollIntoView({ block: 'center', behavior: 'smooth' });
        empty.focus({ preventScroll: true });
        say(`Fill in ${labelOf(empty)} first.`);
        return;
      }
      const buttons = [el.submit, el.barBtn].filter(Boolean);
      buttons.forEach((b) => { b.disabled = true; });
      try {
        await addFromForm();
        lastCount = Number((document.getElementById('bag-count') || {}).textContent) || lastCount;
        const chosen = choiceNames.map((name) => (isSize(name) ? shortSize(state.picks[name]) : state.picks[name])).filter(Boolean);
        say(`Added to your bag: ${[product.title, ...chosen].join(', ')}`);
      } catch (err) {
        say(err.message || SORRY);
      } finally {
        buttons.forEach((b) => { b.disabled = false; });
      }
    }
    // A personalised piece is sent by the personalizer app: it builds the preview, then submits the
    // form itself and the page goes to the bag. Adding it here as well would put it in twice.
    const personal = String(form.dataset.kind || '').split(' ').includes('custom');
    if (!personal) form.addEventListener('submit', (e) => { e.preventDefault(); addToBag(); });
    // The phone bar presses the main button, so whatever an app hangs on that button (a personalizer
    // building its preview) runs for the bar too. While a choice is open it takes her to it instead.
    if (el.barBtn) el.barBtn.addEventListener('click', () => { if (el.submit && !el.submit.disabled) el.submit.click(); else addToBag(); });
    // The store's cart drawer app (Zipify) takes over the form's submit and adds the item itself, so
    // the bag count is read back from the cart a moment after any add, whoever did the adding.
    let lastCount = Number(SHOP.itemCount) || 0;
    const drawerOpen = () => [...document.querySelectorAll('.ocu-cart-drawer, [class*="cart-drawer"]')].some((d) => getComputedStyle(d).visibility !== 'hidden' && getComputedStyle(d).display !== 'none');
    async function settleBag() {
      try {
        const res = await fetch(CART_JSON, { headers: { Accept: 'application/json' } });
        if (!res.ok) return;
        const cart = await res.json();
        const count = Number(cart.item_count) || 0;
        if (count === lastCount) return;
        const grew = count > lastCount;
        lastCount = count;
        writeBag(count);
        if (grew && !drawerOpen()) {
          const chosen = choiceNames.map((name) => (isSize(name) ? shortSize(state.picks[name] || '') : state.picks[name])).filter(Boolean);
          say(`Added to your bag: ${product.title}${chosen.length ? `, ${chosen.join(', ')}` : ''}`);
        }
      } catch (err) {
        // Offline: the count on the page stands.
      }
    }
    // Zipify stops the click before it reaches the button, so the watch is set at the window, which
    // hears every click first. An app's own button in the buy box (the bundle builder's Add to Cart)
    // is watched too: it adds by itself and tells no one, so the count and the toast come from here.
    const watchBag = () => { window.setTimeout(settleBag, 1200); window.setTimeout(settleBag, 3000); };
    window.addEventListener('click', (e) => {
      if (e.target instanceof Element && e.target.closest('[data-buy] [data-add], [data-bar-btn], .pdp__app button')) watchBag();
    }, true);
    // After an add made here (quick add of the piece that goes with it, say), the badge is already right and
    // the toast already said what went in: the count is only taken up, so settleBag does not name this page's product.
    document.addEventListener('sbg:cart:added', () => { lastCount = Number((document.getElementById('bag-count') || {}).textContent) || lastCount; });

    // The photo counter on a phone.
    if (el.count && el.photos) {
      const total = el.photos.children.length;
      const update = () => {
        const item = el.photos.querySelector('li');
        if (!item) return;
        const step = item.getBoundingClientRect().width + 8;
        const index = Math.min(total, Math.max(1, Math.round(el.photos.scrollLeft / step) + 1));
        el.count.textContent = `${index} / ${total}`;
      };
      el.photos.addEventListener('scroll', update, { passive: true });
      update();
    }

    // The phone buy bar appears once the main button has scrolled away.
    if (el.bar && el.submit && 'IntersectionObserver' in window) {
      const watch = new IntersectionObserver(([entry]) => {
        const gone = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        el.bar.toggleAttribute('data-show', gone);
        document.body.toggleAttribute('data-buybar', gone);
      });
      watch.observe(el.submit);
    }

    // The size guide: the chart for this kind of garment, in inches or centimetres.
    const charts = window.SBG_SIZE_CHARTS || {};
    const chart = guide ? charts[guide.getAttribute('data-chart')] : null;
    const tables = guide ? guide.querySelector('[data-guide-tables]') : null;
    if (guide && tables && chart) showUnits(guide, (unit) => { tables.innerHTML = chartHtml(chart, unit); });

    render(state);
  }

  // ----- size charts: the product page's guide and the size guide page draw them the same way -----
  function chartHtml(chart, unit) {
    return chart.families.map((f) => `<h3>${esc(f.name)}</h3>
<table>
  <thead><tr><th scope="col">Size</th>${f.sizes.map((s) => `<th scope="col">${esc(s)}</th>`).join('')}</tr></thead>
  <tbody>${Object.entries(f.rows).map(([row, values]) => `<tr><th scope="row">${esc(row)}</th>${values.map((v) => `<td>${measure(v, unit)}</td>`).join('')}</tr>`).join('')}</tbody>
</table>${f.tolerance ? `<p class="guide__note">Give or take ${measure(f.tolerance, unit)} ${unit === 'cm' ? 'cm' : 'in'}.</p>` : ''}`).join('');
  }
  // The inches and centimetres buttons: draw in the one pressed, and mark it pressed.
  function showUnits(root, draw) {
    const pick = (unit) => {
      draw(unit);
      root.querySelectorAll('[data-unit]').forEach((b) => b.setAttribute('aria-pressed', String(b.getAttribute('data-unit') === unit)));
    };
    root.querySelectorAll('[data-unit]').forEach((b) => b.addEventListener('click', () => pick(b.getAttribute('data-unit'))));
    pick('in');
  }

  // ----- the size guide page: every chart, one after another -----
  const chartsPage = document.querySelector('[data-charts]');
  const chartsBox = chartsPage ? chartsPage.querySelector('[data-charts-tables]') : null;
  if (chartsPage && chartsBox) {
    const all = Object.values(window.SBG_SIZE_CHARTS || {});
    showUnits(chartsPage, (unit) => {
      chartsBox.innerHTML = all.map((c) => `<section class="chart"><h2>${esc(c.title)}</h2>${chartHtml(c, unit)}</section>`).join('');
    });
  }
})();